import React, { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { 
  FaUserShield, 
  FaSignOutAlt, 
  FaBars, 
  FaTimes as FaClose,
  FaChartLine,
  FaUsers,
  FaFileAlt,
  FaProjectDiagram,
  FaComments,
  FaCogs,
  FaEnvelope,
  FaMobile,
  FaLink,
  FaAd,
  FaHeadset,
  FaGlobe
} from 'react-icons/fa';
import EmailVerificationWarning from '../EmailVerificationWarning';

const AdminLayout = ({ children, user, onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showEmailWarning, setShowEmailWarning] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // User kontrolü - eğer user yoksa login'e yönlendir
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const menuItems = [
    { id: 'overview', label: 'Genel Bakış', icon: FaChartLine, path: '/admin' },
    { id: 'users', label: 'Kullanıcılar', icon: FaUsers, path: '/admin/users' },
    { id: 'documents', label: 'Dökümanlar', icon: FaFileAlt, path: '/admin/documents' },
    { id: 'projects', label: 'Projeler', icon: FaProjectDiagram, path: '/admin/projects' },
    { id: 'shortlinks', label: 'Short Links', icon: FaLink, path: '/admin/shortlinks' },
    { id: 'internal-ads', label: 'Internal Ads', icon: FaAd, path: '/admin/internal-ads' },
    { id: 'support-tickets', label: 'Support Tickets', icon: FaHeadset, path: '/admin/support-tickets' },
    { id: 'landing-pages', label: 'Landing Pages', icon: FaGlobe, path: '/admin/landing-pages' },
    { id: 'contact-messages', label: 'İletişim', icon: FaComments, path: '/admin/contact-messages' },
    { id: 'playstore-emails', label: 'Play Store Emails', icon: FaMobile, path: '/admin/playstore-emails' },
    { id: 'email', label: 'Email Yönetimi', icon: FaEnvelope, path: '/admin/email' },
    { id: 'plan-limits', label: 'Plan Limitleri', icon: FaCogs, path: '/admin/plan-limits' }
  ];

  const getCurrentMenuItem = () => {
    const currentPath = location.pathname;
    if (currentPath === '/admin') return 'overview';
    if (currentPath === '/admin/users') return 'users';
    if (currentPath === '/admin/documents') return 'documents';
    if (currentPath === '/admin/projects') return 'projects';
    if (currentPath === '/admin/shortlinks') return 'shortlinks';
    if (currentPath === '/admin/internal-ads') return 'internal-ads';
    if (currentPath === '/admin/support-tickets') return 'support-tickets';
    if (currentPath === '/admin/landing-pages') return 'landing-pages';
    if (currentPath === '/admin/contact-messages') return 'contact-messages';
    if (currentPath === '/admin/playstore-emails') return 'playstore-emails';
    if (currentPath === '/admin/email') return 'email';
    if (currentPath === '/admin/plan-limits') return 'plan-limits';
    return 'overview';
  };

  const getPageTitle = () => {
    const currentPath = location.pathname;
    if (currentPath === '/admin') return 'Genel Bakış';
    if (currentPath === '/admin/users') return 'Kullanıcı Yönetimi';
    if (currentPath === '/admin/documents') return 'Döküman Yönetimi';
    if (currentPath === '/admin/projects') return 'Proje Yönetimi';
    if (currentPath === '/admin/shortlinks') return 'Short Link Yönetimi';
    if (currentPath === '/admin/internal-ads') return 'Internal Ads Yönetimi';
    if (currentPath === '/admin/support-tickets') return 'Support Ticket Yönetimi';
    if (currentPath === '/admin/landing-pages') return 'Landing Page Yönetimi';
    if (currentPath === '/admin/contact-messages') return 'İletişim Mesajları';
    if (currentPath === '/admin/playstore-emails') return 'Play Store Developer Emails';
    if (currentPath === '/admin/email') return 'Email Yönetimi';
    if (currentPath === '/admin/plan-limits') return 'Plan Limitleri';
    return 'Admin Panel';
  };

  const handleMenuClick = (path) => {
    navigate(path);
    setSidebarOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    onLogout();
  };

  return (
    <div className="min-h-screen bg-gray-900 flex">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-gray-800 transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 flex flex-col h-screen ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-700 flex-shrink-0">
          <div className="flex items-center">
            <FaUserShield className="h-8 w-8 text-blue-500" />
            <span className="ml-3 text-xl font-bold text-white">Admin Panel</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-gray-400 hover:text-white"
          >
            <FaClose className="h-6 w-6" />
          </button>
        </div>

        <nav className="mt-8 px-4 flex-1 overflow-y-auto">
          <div className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = getCurrentMenuItem() === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleMenuClick(item.path)}
                  className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                    isActive
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  <Icon className="mr-3 h-5 w-5" />
                  {item.label}
                </button>
              );
            })}
          </div>
        </nav>

        <div className="p-4 border-t border-gray-700 flex-shrink-0">
          <button
            onClick={handleLogout}
            className="w-full flex items-center px-4 py-3 text-sm font-medium text-red-400 hover:bg-red-900/20 rounded-lg transition-colors"
          >
            <FaSignOutAlt className="mr-3 h-5 w-5" />
            Çıkış Yap
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1">
        {/* Header */}
        <div className="bg-gray-800 border-b border-gray-700">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden text-gray-400 hover:text-white mr-4"
                >
                  <FaBars className="h-6 w-6" />
                </button>
                <h1 className="text-2xl font-bold text-white">
                  {getPageTitle()}
                </h1>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-gray-900 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-white">
                      {user?.firstName?.charAt(0) || 'A'}{user?.lastName?.charAt(0) || 'D'}
                    </span>
                  </div>
                  <span className="text-gray-300 text-sm">
                    {user?.firstName || 'Admin'} {user?.lastName || 'User'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Page Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(100vh-8rem)]">
          {/* Email Verification Warning */}
          {user && !user.emailVerified && showEmailWarning && (
            <EmailVerificationWarning 
              user={user} 
              onClose={() => setShowEmailWarning(false)} 
            />
          )}
          {children}
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
